import React, { useCallback, useState } from 'react';
import cl from 'classnames';

import type {
  CompanyTabNanoID,
  CompanyTabUUID
} from '../../../companyTabsTypes';
import { TeamNanoID } from '../../../../teams/teamsTypes';

import { DELETE_COMPANY_TAB_QUERY } from '../../../queries/deleteCompanyTab.query';
import { UPDATE_COMPANY_TAB_QUERY } from '../../../queries/updateCompanyTab.query';

import { useDeleteCompanyTab } from '../../../hooks/useDeleteCompanyTab';
import { useUpdateCompanyTab } from '../../../hooks/useUpdateCompanyTab';

import { useThirdPersonView } from '../../../../../app/hooks/useThirdPersonView';

import { ProfileInputEditableField } from '../../../../teams/helpers/ProfileInputEditableField';

import { ConfirmModalButton } from '../../../../../helpers/buttons/ConfirmModalButton';
import { NextPureLinkHelper } from '../../../../../helpers/links/NextPureLinkHelper';
import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';

import { CompanyTabsCache } from '../../../CompanyTabsCache';
import { words, stringsKeys } from '../../../../../locales/keys';

import { IconsEnum } from '../../../../../assets/icons/types';
import { TeamPath } from '../../../../teams/TeamPath';

interface CompanyTabsMenuDropdownItemProps {
  active: boolean;
  name: string;
  onDeleteActiveTab?: () => void;
  onSelectTab?: () => void;
  companyTabNanoId: CompanyTabNanoID;
  companyTabUuid: CompanyTabUUID;
  companyNanoId: TeamNanoID;
  withUpdateButton: boolean;
  withDeleteButton: boolean;
}

function CompanyTabsMenuDropdownItem({
  active,
  name,
  onDeleteActiveTab,
  onSelectTab,
  companyTabNanoId,
  companyTabUuid,
  companyNanoId,
  withUpdateButton,
  withDeleteButton
}: CompanyTabsMenuDropdownItemProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const { isThirdPersonView } = useThirdPersonView();

  const tabHref = isThirdPersonView
    ? TeamPath.companyTab(companyNanoId, companyTabNanoId)
    : TeamPath.currentCompanyTab(companyTabNanoId);

  const {
    updateCompanyTab,
    updateCompanyTabLoading,
    updateCompanyTabErrorMessage,
    updateCompanyTabReset
  } = useUpdateCompanyTab({
    query: UPDATE_COMPANY_TAB_QUERY,
    cacheKeys: [CompanyTabsCache.indexCacheKey()]
  });

  const {
    deleteCompanyTab,
    deleteCompanyTabLoading,
    deleteCompanyTabErrorMessage,
    deleteCompanyTabReset
  } = useDeleteCompanyTab({
    query: DELETE_COMPANY_TAB_QUERY,
    cacheKeys: [CompanyTabsCache.indexCacheKey()]
  });

  const handleUpdateTab = useCallback<
    (updatedName: string) => Promise<unknown>
  >(
    (updatedName) => {
      const trimmedName = updatedName.trim();
      if (!trimmedName || trimmedName === name) {
        setIsEditing(false);
        return Promise.resolve();
      }
      return updateCompanyTab({
        uid: companyTabUuid,
        name: trimmedName
      });
    },
    [updateCompanyTab, companyTabUuid, name]
  );

  const handleDeleteTab = useCallback<() => Promise<void>>(
    () =>
      deleteCompanyTab({ uid: companyTabUuid }).then(() => {
        if (active) {
          onDeleteActiveTab?.();
        }
      }),
    [active, deleteCompanyTab, companyTabUuid, onDeleteActiveTab]
  );

  return (
    <li
      className={cl(
        'bg-gray-500 duration-150 ease-in-out flex group items-center py-2 relative transition-colors -mx-4 pl-6 pr-4',
        active
          ? 'bg-opacity-25 hover:bg-opacity-30'
          : 'bg-opacity-0 hover:bg-opacity-20'
      )}
    >
      {isEditing ? (
        <ProfileInputEditableField
          className="w-full"
          inputClassName="number-appearance-none leading-none border-0 text-sm py-1 px-2 w-full rounded placeholder:italic bg-transparent dark:placeholder:text-gray-600 hover:bg-gray-200 dark:focus:bg-gray-800 dark:hover:bg-gray-800"
          fieldName="editCompanyTab"
          defaultValue={name}
          onSubmit={handleUpdateTab}
          showEditIcon={false}
          disabled={updateCompanyTabLoading}
          onResetEdit={() => setIsEditing(false)}
          errorMessage={updateCompanyTabErrorMessage}
          autoFocus
        />
      ) : (
        <>
          <NextPureLinkHelper
            className="focus:outline-none flex-1 px-2 text-sm truncate"
            href={tabHref}
            text={name}
            onClick={onSelectTab}
          />

          <div className="flex items-center space-x-1">
            {withUpdateButton && (
              <PureIconButtonHelper
                icon={IconsEnum.PENCIL}
                onClick={() => {
                  updateCompanyTabReset();
                  setIsEditing(true);
                }}
                disabled={updateCompanyTabLoading || deleteCompanyTabLoading}
                className="sm:absolute sm:opacity-0 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus:ring-offset-0 group-hover:relative group-hover:opacity-100 focus:relative focus:opacity-100 rounded-full z-10"
              />
            )}

            {withDeleteButton && (
              <ConfirmModalButton
                className="sm:absolute sm:opacity-0 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus:ring-offset-0 group-hover:relative group-hover:opacity-100 focus:relative focus:opacity-100 rounded-full z-10"
                errorMessage={deleteCompanyTabErrorMessage}
                i18nSubmitText={words.delete}
                i18nText={stringsKeys.areYouSureYouWantToDeleteTab}
                i18nTitle={words.delete}
                icon={IconsEnum.X}
                id={`delete-company-tab-modal-${companyTabNanoId}`}
                isLoading={deleteCompanyTabLoading}
                modalIcon={IconsEnum.EXCLAMATION}
                modalIconClassName="h-6 w-6 text-red-600"
                onClose={deleteCompanyTabReset}
                onSubmit={handleDeleteTab}
              />
            )}
          </div>
        </>
      )}
    </li>
  );
}

export default CompanyTabsMenuDropdownItem;
